/**
 * useProductSearch Hook
 * Primary data source for Products grid - fetches from BigQuery-backed productSearch API.
 * Replaces useAllProductsPagination for immediate data loading without tab dependency.
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';
const STORAGE_KEY = 'products-grid-page-size';

/**
 * Hook for fetching products from productSearch Cloud Function
 * Loads data immediately on mount - no external dependencies required.
 *
 * @param {Object} options - Hook configuration
 * @param {number} options.initialPageSize - Initial page size (default 50)
 * @returns {Object} Products state and pagination controls
 */
export const useProductSearch = ({ initialPageSize = 50 } = {}) => {
    // State management
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [pageSize, setPageSizeState] = useState(() => {
        // Initialize from localStorage if available
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? parseInt(stored, 10) : initialPageSize;
    });
    const [filters, setFilters] = useState({});

    // AbortController ref for canceling in-flight requests
    const abortControllerRef = useRef(null);
    // Request ID counter to track which request is current
    const requestIdRef = useRef(0);

    /**
     * Fetch products from productSearch API
     * Handles request cancellation and error states
     */
    const fetchProducts = useCallback(async (page, size, filterParams) => {
        // Cancel any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        // Increment request ID to track this specific request
        const currentRequestId = ++requestIdRef.current;

        setLoading(true);
        setError(null);

        const offset = (page - 1) * size;
        const params = new URLSearchParams({
            limit: size.toString(),
            offset: offset.toString(),
        });

        // Add filter parameters
        if (filterParams) {
            Object.entries(filterParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });
        }

        try {
            const url = `${API_BASE}/productSearch?${params}`;
            console.log('[useProductSearch] Fetching:', url);

            const response = await fetch(url, {
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`Search failed: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            console.log('[useProductSearch] Response:', {
                success: data.success,
                total: data.total,
                productCount: data.products?.length,
                hasMore: data.hasMore
            });

            // Check if this request is still current (hasn't been superseded by a newer request)
            if (currentRequestId !== requestIdRef.current) {
                console.log('[useProductSearch] Ignoring stale response for request', currentRequestId);
                return;
            }

            // Transform products for grid compatibility
            // The productSearch API returns product families with variants
            const transformedProducts = transformProductsForGrid(data.products || []);

            setProducts(transformedProducts);
            setTotalProducts(data.total || 0);
            setCurrentPage(page);
            setLoading(false);

            return transformedProducts;
        } catch (err) {
            // Handle abort errors - they're expected when canceling requests
            if (err.name === 'AbortError') {
                console.log('[useProductSearch] Request aborted for request', currentRequestId);
                // Only reset loading if this was the most recent request
                // (no newer request has been made)
                if (currentRequestId === requestIdRef.current) {
                    setLoading(false);
                }
                return;
            }

            // For other errors, only update state if this is still the current request
            if (currentRequestId !== requestIdRef.current) {
                console.log('[useProductSearch] Ignoring stale error for request', currentRequestId);
                return;
            }

            console.error('[useProductSearch] Error:', err);
            setError(err.message);
            setLoading(false);
            return [];
        }
    }, []);

    /**
     * Transform products from API response to grid format
     * Flattens variant structure and adds parent data to each variant
     *
     * API returns product families with variants array:
     * - Each variant has: docId, sku, parentSku, size, _firestorePath, _sourceTable
     * - Variants from variants_raw_latest have full Firestore path for inline editing
     * - Variants from products_raw_latest (fallback) use document_name as docId
     */
    const transformProductsForGrid = (products) => {
        const transformedRows = [];
        let variantCount = 0;
        let familyCount = 0;

        products.forEach(product => {
            familyCount++;
            // Family-level thumbnail (from core SKU, or propagated by backend)
            const familyThumbnail = product.thumbnail;

            // If product has variants array, flatten it
            if (product.variants && Array.isArray(product.variants)) {
                // Sort variants: core SKU first (matches base_sku), then alphabetically
                const sortedVariants = [...product.variants].sort((a, b) => {
                    const baseSku = product.group_id || product.base_sku;
                    const aIsCore = a.sku === baseSku ? 0 : 1;
                    const bIsCore = b.sku === baseSku ? 0 : 1;
                    if (aIsCore !== bIsCore) return aIsCore - bIsCore;
                    return (a.sku || '').localeCompare(b.sku || '', undefined, { numeric: true });
                });
                sortedVariants.forEach(variant => {
                    variantCount++;
                    // Image inheritance: variant's own → family thumbnail (core SKU image)
                    const resolvedImage = variant.thumbnail || variant.primaryImage || familyThumbnail;
                    transformedRows.push({
                        ...variant,
                        // Include parent product data
                        parent_docId: product.group_id || product.base_sku,
                        parentSku: variant.parentSku || product.base_sku || product.sku,
                        // Copy parent fields that might be needed
                        enrichment: variant.enrichment || product.enrichment,
                        shopify: product.shopify || variant.shopify,
                        shopify_product_id: product.shopify_product_id || variant.shopify_product_id,
                        // Ensure docId is set for grid row identification
                        docId: variant.docId || variant.id,
                        // Map image field for grid (with family inheritance)
                        primaryImage: resolvedImage,
                        thumbnail: resolvedImage,
                        // Firestore path for inline editing (from API or constructed)
                        _firestorePath: variant._firestorePath ||
                            `productCatalog/topicz/products/${variant.parentSku || product.base_sku}/variants/${variant.docId}`,
                        // Track data source
                        _sourceTable: variant._sourceTable || 'unknown',
                    });
                });
            } else {
                // Product is already flat (single variant or already transformed)
                variantCount++;
                transformedRows.push({
                    ...product,
                    docId: product.docId || product.id,
                    parent_docId: product.parent_docId || product.base_sku || product.docId || product.id,
                    parentSku: product.parentSku || product.base_sku,
                    primaryImage: product.thumbnail || product.primaryImage,
                    _firestorePath: product._firestorePath ||
                        `productCatalog/topicz/products/${product.docId || product.id}`,
                });
            }
        });

        console.log(`[useProductSearch] Flattening ${variantCount} variants from ${familyCount} families`);
        return transformedRows;
    };

    /**
     * Initial load effect - fetches products on mount
     * Re-fetches when pageSize or filters change
     */
    useEffect(() => {
        console.log('[useProductSearch] Initial/dependency load:', { pageSize, filters });
        fetchProducts(1, pageSize, filters);

        // Cleanup: abort in-flight request on unmount or dependency change
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [pageSize, filters, fetchProducts]);

    /**
     * Update page size and persist to localStorage
     * Resets to page 1 when page size changes
     */
    const setPageSize = useCallback((size) => {
        setPageSizeState(size);
        localStorage.setItem(STORAGE_KEY, size.toString());
        setCurrentPage(1); // Reset to page 1 when changing page size
    }, []);

    /**
     * Navigate to specific page
     */
    const goToPage = useCallback((page) => {
        const totalPages = Math.ceil(totalProducts / pageSize);
        if (page < 1 || page > totalPages) return;
        fetchProducts(page, pageSize, filters);
    }, [totalProducts, pageSize, filters, fetchProducts]);

    /**
     * Navigate to next page
     */
    const goToNextPage = useCallback(() => {
        const totalPages = Math.ceil(totalProducts / pageSize);
        if (currentPage < totalPages) {
            goToPage(currentPage + 1);
        }
    }, [currentPage, totalProducts, pageSize, goToPage]);

    /**
     * Navigate to previous page
     */
    const goToPrevPage = useCallback(() => {
        if (currentPage > 1) {
            goToPage(currentPage - 1);
        }
    }, [currentPage, goToPage]);

    /**
     * Refresh current page data
     */
    const refresh = useCallback(() => {
        fetchProducts(currentPage, pageSize, filters);
    }, [currentPage, pageSize, filters, fetchProducts]);

    /**
     * Update filters (resets to page 1)
     */
    const updateFilters = useCallback((newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1); // Reset to page 1 when filters change
    }, []);

    // Computed values
    const totalPages = Math.ceil(totalProducts / pageSize);

    return {
        // Data state
        products,
        loading,
        error,

        // Pagination state
        currentPage,
        totalProducts,
        totalPages,
        pageSize,

        // Filter state
        filters,

        // Actions
        setPageSize,
        setFilters: updateFilters,
        goToPage,
        goToNextPage,
        goToPrevPage,
        refresh,
    };
};

export default useProductSearch;
